/** Replay and experiments: a read-only list of registered runs, with no result and no profit badge (U066-U070). */
import { decodeReplayRow, type ReplayRunRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { mountOps, shortHash } from "../ops-common.ts";

function statusTone(status: ReplayRunRow["status"]): "ok" | "warn" | "bad" | "info" {
  switch (status) {
    case "COMPLETED":
      return "ok";
    case "FAILED":
      return "bad";
    case "RUNNING":
    case "QUEUED":
      return "warn";
    case "REGISTERED":
    case "CANCELLED":
      return "info";
  }
}

function run(row: ReplayRunRow): HTMLElement {
  return h(
    "article",
    { class: "panel run", attrs: { "data-run": row.runId, "aria-labelledby": `run-${row.runId}` } },
    h("h2", { attrs: { id: `run-${row.runId}` } }, `${row.runId}: ${row.title}`),
    h("p", {}, badge(row.status, statusTone(row.status))),
    definitions([
      ["Dataset (frozen)", shortHash(row.datasetRef)],
      ["Universe", row.universe],
      ["Strategy version", row.strategyVersion],
      ["Cost model", row.costVersion],
      ["Calendar", row.calendarVersion],
      ["Fill model", row.fillVersion],
      ["Chronology", row.chronology],
      [
        "Evaluation split",
        row.evaluationSplitUntouched ? "untouched" : "TOUCHED: the split is no longer clean",
      ],
      ["Trials attempted", String(row.trialsAttempted)],
      ["Seeds", row.seeds.join(", ")],
      ["Manifest hash", shortHash(row.manifestHash)],
      ["Reproducibility", row.reproducibility.replace("_", " ").toLowerCase()],
      ["Unresolved exits", String(row.unresolvedExits)],
      ["Submitted", instantCell(row.submittedAt)],
      ["Started", instantCell(row.startedAt)],
      ["Finished", instantCell(row.finishedAt)],
      ["Failure reason", metric(row.failureReason)],
    ]),
  );
}

/** Draw the registered runs. */
export function drawReplay(rows: readonly ReplayRunRow[]): readonly Child[] {
  return [
    h(
      "p",
      { class: "note" },
      "Registered runs are listed read-only. Their frozen inputs and state are shown; an outcome is not rated or scored here, and running one is not offered in this release.",
    ),
    ...rows.map(run),
  ];
}

/** Mount the replay list. */
export function mountReplay(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "replay",
    title: "Replay and experiments",
    projection: "replay",
    row: decodeReplayRow,
    draw: (rows) => drawReplay(rows),
  });
}
